import { useTranslation } from "react-i18next";
import React, { useEffect } from "react";
import LogoText from "../assets/logo_text.svg?react";
import { FieldGroup, Fieldset } from "./base/fieldset";
import { Button, PrimaryButton } from "src/components/base/button";
import { Skeleton } from "src/components/base/skeleton";
import { GetAdminLoginsResponseItem } from "src/api/generated/frontend_public_v1";
import { ApiPublic } from "src/api/api_public";

/**
 * The properties for {@link Login}
 */
export type LoginProps = {
    /** The function that should be executed on a successful login */
    onLogin: () => void;
};

/**
 * The login component
 */
export default function Login(props: LoginProps) {
    const [t, i18n] = useTranslation("login");

    const [providers, setProviders] = React.useState<Array<GetAdminLoginsResponseItem>>([]);
    const [loading, setLoading] = React.useState(true);

    useEffect(() => {
        (async () => {
            const result = await ApiPublic.adminAuth.getLogins();
            setProviders(result.list);
            setLoading(false);
        })();
    }, []);

    return (
        <div className={"flex h-screen w-full items-center justify-center bg-zinc-50 p-3 dark:bg-neutral-950"}>
            <div className="w-full max-w-md rounded-xl border bg-white dark:border-zinc-800 dark:bg-zinc-900 dark:before:pointer-events-none forced-colors:outline">
                <div
                    className={
                        "grid h-full w-full place-items-start justify-items-center overflow-hidden p-6 py-8 sm:p-8 lg:p-12"
                    }
                >
                    <LogoText
                        className={
                            "h-24 max-w-full p-3 text-[#233F94] drop-shadow-lg dark:text-white dark:drop-shadow-none"
                        }
                    />
                    <div className={"mt-12 flex w-full max-w-sm space-y-8 overflow-hidden p-1"}>
                        <Fieldset className={"w-full"}>
                            <FieldGroup>
                                <div className={"flex flex-col gap-3"}>
                                    {loading ? (
                                        <>
                                            <Skeleton height={36} />
                                            <Skeleton height={36} />
                                        </>
                                    ) : providers.length === 0 ? (
                                        <p className={"text-center text-sm text-zinc-500 dark:text-zinc-400"}>
                                            {t("button.no-providers")}
                                        </p>
                                    ) : (
                                        providers.map((provider) => {
                                            const localizedProviderName =
                                                i18n.language === "de"
                                                    ? provider.login_button_display_german
                                                    : provider.login_button_display_english;
                                            return provider.primary ? (
                                                <PrimaryButton
                                                    key={provider.uuid}
                                                    className={"w-full"}
                                                    href={`/api/frontend/v1/oidc/begin-admin-login/${provider.uuid}`}
                                                    external={true}
                                                >
                                                    {localizedProviderName}
                                                </PrimaryButton>
                                            ) : (
                                                <Button
                                                    key={provider.uuid}
                                                    outline={true}
                                                    className={"w-full"}
                                                    href={`/api/frontend/v1/oidc/begin-admin-login/${provider.uuid}`}
                                                    external={true}
                                                >
                                                    {localizedProviderName}
                                                </Button>
                                            );
                                        })
                                    )}

                                    <Button
                                        outline={true}
                                        className={"w-full"}
                                        href={"/api/frontend/v1/oidc/begin-trufflepig-login"}
                                        external={true}
                                    >
                                        {t("button.sign-in-with-sso-trufflepig")}
                                    </Button>
                                </div>
                            </FieldGroup>
                        </Fieldset>
                    </div>
                </div>
            </div>
        </div>
    );
}
